import { useEffect, useMemo, useState } from 'react'
import { ArrowLeft, ChevronDown, Plus, Trash2, Workflow, X } from 'lucide-react'
import { scopePortfolio, type PfAccount, type PfFill, type PfOrder, type PortfolioLive } from '@shared/portfolio'
import { brokerApi } from '../../lib/api'
import { useStore } from '../../store'
import { dirClass, fmtMoney, fmtPct, fmtPrice } from '../../lib/format'
import { cn } from '../../lib/utils'
import { usePortfolioLive } from '../../hooks/usePortfolioLive'
import { OrderTicket } from './OrderTicket'
import { WatchlistCard } from './WatchlistCard'
import { SymbolSearch } from './SymbolSearch'
import { PortfolioChart } from './PortfolioChart'
import { AllocationDonut } from './AllocationDonut'
import { ViewHeader } from '../shell/ViewHeader'
import { StopAllWorkers } from './IbkrPanel'

function AddHolding({ onDone }: { onDone: () => void }) {
  const addPosition = useStore((s) => s.addPosition)
  const [symbol, setSymbol] = useState('')
  const [qty, setQty] = useState('')
  const [cost, setCost] = useState('')
  const input =
    'min-w-0 flex-1 rounded-lg border border-[var(--line-soft)] bg-[var(--color-inset)] px-2.5 py-1.5 text-[12.5px] text-fg placeholder:text-fg-faint focus:outline-none'
  return (
    <div className="flex flex-wrap items-center gap-1.5 border-b border-[var(--line-soft)] p-3">
      <div className="w-44">
        <SymbolSearch value={symbol} onSelect={setSymbol} placeholder="Symbol or name" />
      </div>
      <input value={qty} onChange={(e) => setQty(e.target.value)} placeholder="Quantity" inputMode="decimal" className={cn(input, 'num w-24')} />
      <input value={cost} onChange={(e) => setCost(e.target.value)} placeholder="Avg cost" inputMode="decimal" className={cn(input, 'num w-24')} />
      <button
        onClick={() => {
          const s = symbol.trim().toUpperCase()
          if (s && Number(qty) > 0) {
            void addPosition(s, Number(qty), Number(cost) || 0)
            onDone()
          }
        }}
        className="rounded-lg bg-[var(--color-accent)] px-3 py-1.5 text-[12px] font-semibold text-[var(--color-base)]"
      >
        Add
      </button>
    </div>
  )
}


const signed = (n: number | null | undefined) => (n == null ? '—' : `${n >= 0 ? '+' : '−'}${fmtMoney(Math.abs(n))}`)
const SESSION: Record<string, string> = { pre: 'Pre-market', after: 'After hours', closed: 'Market closed' }
const qtyText = (n: number) => (Number.isInteger(n) ? String(n) : n.toFixed(6).replace(/0+$/, ''))

function Badge({ children, tone }: { children: React.ReactNode; tone?: 'down' }) {
  return <span className={cn('rounded bg-[var(--color-inset)] px-1 py-px text-[9px] font-semibold uppercase tracking-wide', tone === 'down' ? 'text-down' : 'text-fg-subtle')}>{children}</span>
}
/** "Robinhood · Agentic" → "Agentic": enough to tell accounts apart inside a row. */
const shortName = (a: PfAccount | undefined) => (a ? (a.name.split(' · ').pop() ?? a.name) : '')
const accountBadge = (a: PfAccount) => (a.broker === 'manual' ? 'recorded' : a.paper ? 'paper' : a.tradable ? 'trading' : 'read-only')

/** Every account at a glance; a click narrows the whole page to one (optional — the page starts on all of them). */
function AccountsStrip({ p, onPick }: { p: PortfolioLive; onPick: (id: string) => void }) {
  return (
    <div className="card mt-5 overflow-hidden">
      <div className="grid divide-y divide-[var(--separator)] sm:grid-cols-2 sm:divide-x sm:divide-y-0 lg:grid-cols-3">
        {p.accounts.map((a) => {
          const share = p.totals.value > 0 ? (a.value / p.totals.value) * 100 : 0
          return (
            <button key={a.id} onClick={() => onPick(a.id)} className="group min-w-0 px-3.5 py-3 text-left transition-colors hover:bg-[rgba(255,255,255,0.018)]">
              <div className="flex items-center gap-1.5">
                <span className={cn('h-1.5 w-1.5 shrink-0 rounded-full', a.live ? 'bg-up' : 'bg-flat')} />
                <span className="truncate text-[12.5px] font-medium text-fg">{a.name}</span>
                {a.mask && <span className="num shrink-0 text-[11px] text-fg-faint">••{a.mask.slice(-4)}</span>}
                <span className="ml-auto shrink-0"><Badge>{accountBadge(a)}</Badge></span>
              </div>
              <div className="mt-1.5 flex items-baseline justify-between gap-2">
                <span className="num text-[17px] font-semibold text-fg">{fmtMoney(a.value)}</span>
                <span className={cn('num text-[12px]', dirClass(a.dayPnl))}>{signed(a.dayPnl)} <span className="text-fg-faint">today</span></span>
              </div>
              <div className="mt-2 h-[3px] overflow-hidden rounded-full bg-[var(--color-inset)]">
                <div className="h-full rounded-full bg-[var(--color-fg-subtle)]" style={{ width: `${Math.max(0, Math.min(100, share))}%` }} />
              </div>
              <div className="num mt-1.5 flex items-center justify-between text-[11px] text-fg-subtle">
                <span>{share.toFixed(0)}% of the total</span>
                <span>{a.tradable ? `Buying power ${fmtMoney(a.buyingPower)}` : a.cash != null && a.cash !== 0 ? `Cash ${fmtMoney(a.cash)}` : ''}</span>
              </div>
            </button>
          )
        })}
      </div>
    </div>
  )
}

function Orders({ orders, accounts, showAccount }: { orders: PfOrder[]; accounts: Map<string, PfAccount>; showAccount: boolean }) {
  const openWorker = useStore((s) => s.openWorker)
  const [err, setErr] = useState<string | null>(null)
  if (!orders.length) return <div className="px-4 py-10 text-center text-[12.5px] text-fg-subtle">No working orders.</div>
  const cancel = async (id: number) => {
    setErr(null)
    try {
      await brokerApi.cancelOrder(id)
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Cancel failed')
    }
  }
  return (
    <div className="divide-y divide-[var(--separator)]">
      {orders.map((o) => (
        <div key={o.id} className="flex items-center justify-between gap-2 px-3.5 py-2.5 text-[12.5px]">
          <span className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-0.5">
            <span className={cn('w-8 font-semibold', o.side === 'BUY' ? 'text-up' : 'text-down')}>{o.side}</span>
            <span className="num text-fg">{o.quantity != null ? qtyText(o.quantity) : '—'}</span>
            <span className="font-medium text-fg">{o.symbol}</span>
            <span className="num text-fg-subtle">
              {o.type}
              {o.stop != null && ` stop ${fmtPrice(o.stop)}`}
              {o.limit != null && ` limit ${fmtPrice(o.limit)}`}
              {o.tif ? ` · ${o.tif}` : ''}
            </span>
            <span className="text-[11px] text-fg-faint">
              {o.status}
              {o.filled > 0 ? ` · ${qtyText(o.filled)} filled` : ''}
            </span>
            {o.workerId ? (
              <button onClick={() => openWorker(o.workerId!)} className="inline-flex items-center gap-1 rounded bg-[var(--color-inset)] px-1.5 py-0.5 text-[10.5px] text-fg-muted hover:text-fg" title="Placed by a trade worker — manage it from the worker">
                <Workflow size={10} /> worker
              </button>
            ) : o.ticketId ? (
              <Badge>chat order</Badge>
            ) : o.orderId == null ? (
              <Badge>placed at the broker</Badge>
            ) : null}
            {showAccount && <span className="text-[11px] text-fg-faint">{shortName(accounts.get(o.account))}</span>}
          </span>
          {o.orderId != null && !o.workerId ? (
            <button onClick={() => void cancel(o.orderId!)} className="shrink-0 rounded p-1 text-fg-subtle hover:text-down" title="Cancel order">
              <X size={14} />
            </button>
          ) : null}
        </div>
      ))}
      {err && <div className="px-3.5 py-2 text-[11.5px] text-down">{err}</div>}
    </div>
  )
}

function Activity({ fills, accounts, showAccount }: { fills: PfFill[]; accounts: Map<string, PfAccount>; showAccount: boolean }) {
  const openWorker = useStore((s) => s.openWorker)
  if (!fills.length) return <div className="px-4 py-10 text-center text-[12.5px] text-fg-subtle">No trades yet.</div>
  const row = (f: PfFill) => (
    <div key={f.id} className="flex items-center justify-between gap-2 px-3.5 py-2 text-[12.5px]">
      <span className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-0.5">
        <span className={cn('w-8 font-semibold', f.side === 'BUY' ? 'text-up' : 'text-down')}>{f.side}</span>
        <span className="num text-fg">{qtyText(f.shares)}</span>
        <span className="font-medium text-fg">{f.symbol}</span>
        <span className="num text-fg-subtle">@ {fmtPrice(f.price)}</span>
        <span className="num text-fg-faint">{fmtMoney(f.shares * f.price)}</span>
        {f.source === 'worker' && f.workerId ? (
          <button onClick={() => openWorker(f.workerId!)} className="inline-flex items-center gap-1 rounded bg-[var(--color-inset)] px-1.5 py-0.5 text-[10.5px] text-fg-muted hover:text-fg">
            <Workflow size={10} /> worker
          </button>
        ) : f.source === 'order' ? (
          <Badge>chat order</Badge>
        ) : f.source === 'recorded' ? (
          <Badge>recorded</Badge>
        ) : null}
        {showAccount && <span className="text-[11px] text-fg-faint">{shortName(accounts.get(f.account))}</span>}
      </span>
      <span className="num shrink-0 text-[11.5px] text-fg-faint">
        {f.today ? new Date(f.at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : new Date(f.at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
      </span>
    </div>
  )
  const today = fills.filter((f) => f.today)
  const earlier = fills.filter((f) => !f.today)
  return (
    <div>
      {today.length > 0 && (
        <>
          <div className="px-3.5 pb-1 pt-2.5 text-[10px] font-semibold uppercase tracking-wide text-fg-faint">Today</div>
          <div className="divide-y divide-[var(--separator)]">{today.map(row)}</div>
        </>
      )}
      {earlier.length > 0 && (
        <>
          <div className="px-3.5 pb-1 pt-2.5 text-[10px] font-semibold uppercase tracking-wide text-fg-faint">Earlier</div>
          <div className="divide-y divide-[var(--separator)]">{earlier.slice(0, 80).map(row)}</div>
        </>
      )}
    </div>
  )
}

function TickerOptions() {
  const settings = useStore((s) => s.settings)
  const updateSettings = useStore((s) => s.updateSettings)
  const [open, setOpen] = useState(false)
  const row = (label: string, key: 'showPortfolioInTicker' | 'showHoldingsInTicker' | 'showWatchlistInTicker') => (
    <label className="flex cursor-pointer items-center gap-2 text-[12px] text-fg-muted">
      <input type="checkbox" checked={settings?.[key] ?? false} onChange={(e) => void updateSettings({ [key]: e.target.checked })} className="accent-[var(--color-accent)]" />
      {label}
    </label>
  )
  return (
    <div className="px-1">
      <button onClick={() => setOpen((v) => !v)} className="text-[12px] font-medium text-fg-subtle hover:text-fg">
        {open ? '▾' : '▸'} Ticker tape
      </button>
      {open && (
        <div className="mt-2 space-y-1.5">
          {row('Total portfolio value', 'showPortfolioInTicker')}
          {row('Each holding’s symbol', 'showHoldingsInTicker')}
          {row('Watchlist symbols', 'showWatchlistInTicker')}
        </div>
      )}
    </div>
  )
}

export function PortfolioPage() {
  const { portfolio: all, streaming, refresh } = usePortfolioLive()
  const portfolioRemove = useStore((s) => s.portfolioRemove)
  const loadPortfolio = useStore((s) => s.loadPortfolio)
  const openWorker = useStore((s) => s.openWorker)
  const focusSettings = useStore((s) => s.focusSettings)
  const isAdmin = useStore((s) => s.user?.role === 'admin')
  const [scope, setScope] = useState<string | null>(null)
  const [scopeOpen, setScopeOpen] = useState(false)
  const [tab, setTab] = useState<'holdings' | 'orders' | 'activity'>('holdings')
  const [adding, setAdding] = useState(false)
  const [selected, setSelected] = useState('')

  useEffect(() => {
    void loadPortfolio() // the ticker tape and the watchlist read the store
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  // An account that went away (a broker disconnected) ends the narrowing.
  useEffect(() => {
    if (scope && all && !all.accounts.some((a) => a.id === scope)) setScope(null)
  }, [scope, all])

  const p = useMemo(() => (all ? scopePortfolio(all, scope) : null), [all, scope])
  const accounts = useMemo(() => new Map((all?.accounts ?? []).map((a) => [a.id, a])), [all])
  const first = p?.holdings[0]?.symbol
  useEffect(() => {
    if (!selected && first) setSelected(first)
  }, [first, selected])

  const changed = () => {
    refresh()
    void loadPortfolio()
  }
  const many = (all?.accounts.length ?? 0) > 1
  const one = scope ? accounts.get(scope) : !many ? all?.accounts[0] : undefined
  const t = p?.totals
  const others = (scope ? (one?.other ?? []) : (all?.accounts ?? []).flatMap((a) => a.other)).reduce((m, o) => m.set(o.label, (m.get(o.label) ?? 0) + o.value), new Map<string, number>())
  const canRecord = !scope || scope === 'manual'

  return (
    <div className="flex h-full min-w-0 flex-1 flex-col overflow-hidden bg-[var(--color-base)]">
      <ViewHeader title="Portfolio" />
      <div className="min-w-0 flex-1 overflow-y-auto overflow-x-hidden">
        <div className="mx-auto w-full max-w-6xl px-3 py-5 sm:px-5 sm:py-6">
          {/* the one number, and what it is made of */}
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                {scope && (
                  <button onClick={() => setScope(null)} className="inline-flex items-center gap-1 rounded-md py-0.5 pr-1.5 text-[12px] text-fg-subtle hover:text-fg" title="Back to all accounts">
                    <ArrowLeft size={13} /> All accounts
                  </button>
                )}
                <h1 className="truncate text-[13px] font-medium text-fg-muted">
                  {scope ? (
                    <>
                      <span className="text-fg-faint">/ </span>
                      {one?.name}
                      {one?.mask && <span className="num text-fg-faint"> ••{one.mask.slice(-4)}</span>}
                    </>
                  ) : many ? (
                    `All accounts · ${all!.accounts.length}`
                  ) : (
                    (one?.name ?? 'Portfolio')
                  )}
                </h1>
                {one && <Badge tone={one.tradable && !one.paper ? 'down' : undefined}>{one.tradable && !one.paper ? 'real money' : accountBadge(one)}</Badge>}
              </div>
              <div className="num mt-1 text-[32px] font-semibold leading-tight tracking-tight text-fg">{p ? fmtMoney(t!.value) : <span className="text-fg-faint">$—</span>}</div>
              <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-[13px]">
                <span className={cn('num', dirClass(t?.dayPnl))}>
                  {signed(t?.dayPnl)} ({fmtPct(t?.dayPct)}) <span className="text-fg-subtle">today</span>
                </span>
                <span className={cn('num', dirClass(t?.openPnl))}>
                  {signed(t?.openPnl)} ({fmtPct(t?.openPnlPct)}) <span className="text-fg-subtle">open P/L</span>
                </span>
                {t && t.cash !== 0 && (
                  <span className="num text-fg-muted">
                    {fmtMoney(t.cash)} <span className="text-fg-subtle">cash</span>
                  </span>
                )}
                {[...others].map(([label, v]) => (
                  <span key={label} className="num text-fg-muted">
                    {fmtMoney(v)} <span className="text-fg-subtle">{label.toLowerCase()}</span>
                  </span>
                ))}
              </div>
            </div>
            <div className="flex flex-col items-end gap-2">
              <div className="flex items-center gap-2 text-[11.5px] text-fg-subtle">
                <span className={cn('h-1.5 w-1.5 rounded-full', p?.live && streaming ? 'bg-up' : 'bg-flat')} />
                {p?.live ? (streaming ? 'Live from the broker' : 'Reconnecting…') : 'Recorded by hand'}
                {p && SESSION[p.session] && <Badge>{SESSION[p.session]}</Badge>}
              </div>
              {many && (
                <div className="relative">
                  <button onClick={() => setScopeOpen((o) => !o)} className="inline-flex items-center gap-1.5 rounded-lg border border-[var(--line-soft)] bg-[var(--color-surface-2)] px-2.5 py-1.5 text-[12px] text-fg-muted hover:text-fg">
                    {scope ? one?.name : 'All accounts'} <ChevronDown size={13} />
                  </button>
                  {scopeOpen && (
                    <>
                      <div className="fixed inset-0 z-30" onClick={() => setScopeOpen(false)} />
                      <div className="absolute right-0 top-9 z-40 w-64 rounded-lg border border-[var(--line)] bg-[var(--color-surface-3)] p-1 shadow-[var(--e-3)]">
                        {[null, ...all!.accounts].map((a) => (
                          <button
                            key={a?.id ?? 'all'}
                            onClick={() => {
                              setScope(a?.id ?? null)
                              setScopeOpen(false)
                            }}
                            className={cn('flex w-full items-center justify-between gap-2 rounded px-2 py-1.5 text-left text-[12.5px] hover:bg-[var(--color-surface-2)]', (a?.id ?? null) === scope ? 'text-fg' : 'text-fg-muted')}
                          >
                            <span className="truncate">{a ? a.name : 'All accounts'}</span>
                            <span className="num shrink-0 text-[11.5px] text-fg-faint">{fmtMoney(a ? a.value : all!.totals.value)}</span>
                          </button>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>

          {!p ? (
            <div className="card mt-5 h-40 animate-pulse" />
          ) : (
            <>
              {many && !scope && <AccountsStrip p={all!} onPick={setScope} />}
              {scope && one && (one.blocked || one.buyingPower != null) && (
                <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-1 rounded-lg border border-[var(--line-soft)] px-3 py-2 text-[12px] text-fg-muted">
                  {one.tradable && <span className="num">Buying power {fmtMoney(one.buyingPower)}</span>}
                  {one.blocked && <span className="text-fg-subtle">{one.blocked}</span>}
                  {isAdmin && one.broker !== 'manual' && (
                    <button onClick={() => focusSettings(one.broker === 'ibkr' ? 'broker' : 'robinhood')} className="ml-auto text-[11.5px] text-fg-subtle hover:text-fg">
                      Connection settings
                    </button>
                  )}
                </div>
              )}

              {(p.holdings.length > 0 || p.accounts.length > 0) && (
                <div className="mt-5 grid gap-5 lg:grid-cols-2">
                  <PortfolioChart currentValue={t!.value} currentCost={t!.cost + t!.cash} account={scope ?? 'all'} label={scope ? `Value · ${one?.name ?? ''}` : 'Portfolio value'} />
                  <AllocationDonut items={p.holdings.map((h) => ({ symbol: h.symbol, weight: h.weight }))} />
                </div>
              )}

              <div className="mt-5 grid gap-5 lg:grid-cols-[1fr_360px]">
                <div className="min-w-0">
                  <div className="card overflow-hidden">
                    <div className="flex items-center justify-between gap-2 border-b border-[var(--line-soft)] px-2 py-1.5">
                      <div className="flex items-center">
                        {(
                          [
                            ['holdings', 'Holdings', p.holdings.length],
                            ['orders', 'Orders', p.orders.length],
                            ['activity', 'Activity', p.fills.filter((f) => f.today).length],
                          ] as const
                        ).map(([id, label, n]) => (
                          <button key={id} onClick={() => setTab(id)} className={cn('rounded-lg px-2.5 py-1.5 text-[13px] font-medium transition-colors', tab === id ? 'text-fg' : 'text-fg-subtle hover:text-fg')}>
                            {label}
                            {n > 0 && <span className={cn('num ml-1.5 text-[11px]', tab === id ? 'text-fg-muted' : 'text-fg-faint')}>{n}</span>}
                          </button>
                        ))}
                      </div>
                      <div className="flex items-center gap-2 pr-1.5">
                        {isAdmin && tab === 'orders' && <StopAllWorkers />}
                        {tab === 'holdings' && canRecord && (
                          <button onClick={() => setAdding((a) => !a)} className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-[12px] text-fg-subtle hover:text-fg" title="A holding you keep at a broker that is not connected here">
                            {adding ? <X size={13} /> : <Plus size={13} />} {adding ? 'Cancel' : p.live ? 'Record a holding' : 'Add holding'}
                          </button>
                        )}
                      </div>
                    </div>

                    {tab === 'holdings' && (
                      <>
                        {adding && (
                          <>
                            <AddHolding onDone={() => { setAdding(false); setTimeout(changed, 400) }} />
                            {p.live && <div className="border-b border-[var(--line-soft)] px-3.5 py-2 text-[11.5px] text-fg-faint">For shares kept somewhere else. What your connected accounts hold appears here by itself.</div>}
                          </>
                        )}
                        {p.holdings.length === 0 ? (
                          <div className="px-4 py-12 text-center text-[13px] text-fg-subtle">
                            {p.live ? 'No positions in ' + (scope ? 'this account' : 'your accounts') + ' yet.' : 'No holdings yet. Add one, use the order ticket, or just tell Libera — “I bought 10 NVDA at 850”.'}
                          </div>
                        ) : (
                          <div className="overflow-x-auto">
                            <table className="w-full text-[13px]">
                              <thead>
                                <tr className="whitespace-nowrap text-[10px] uppercase tracking-wide text-fg-faint">
                                  <th className="px-3 py-2.5 text-left">Symbol</th>
                                  <th className="px-3 py-2.5 text-right">Qty</th>
                                  <th className="px-3 py-2.5 text-right">Avg cost</th>
                                  <th className="px-3 py-2.5 text-right">Last</th>
                                  <th className="px-3 py-2.5 text-right">Today</th>
                                  <th className="px-3 py-2.5 text-right">Value</th>
                                  <th className="px-3 py-2.5 text-right">Open P/L</th>
                                  <th className="hidden px-3 py-2.5 text-right md:table-cell">SL / TP</th>
                                  <th className="hidden px-3 py-2.5 text-right sm:table-cell">Wt</th>
                                  <th className="w-6 px-1 py-2.5"></th>
                                </tr>
                              </thead>
                              <tbody>
                                {p.holdings.map((h) => {
                                  const slHit = h.stopLoss != null && h.last != null && h.last <= h.stopLoss
                                  const tpHit = h.takeProfit != null && h.last != null && h.last >= h.takeProfit
                                  const ws = h.parts.flatMap((x) => x.workers)
                                  const manualOnly = h.parts.every((x) => x.account === 'manual')
                                  return (
                                    <tr key={h.code} onClick={() => setSelected(h.symbol)} className={cn('group cursor-pointer border-t border-[var(--separator)] hover:bg-[rgba(255,255,255,0.018)]', selected === h.symbol && 'bg-[rgba(255,255,255,0.012)]')}>
                                      <td className="px-3 py-2.5">
                                        <div className="font-medium text-fg">{h.symbol}</div>
                                        {(ws.length > 0 || (many && !scope)) && (
                                          <div className="mt-0.5 flex flex-wrap items-center gap-1 text-[10.5px] text-fg-faint">
                                            {many && !scope && <span className="truncate">{h.parts.map((x) => `${shortName(accounts.get(x.account))}${h.parts.length > 1 ? ` ${qtyText(x.quantity)}` : ''}`).join(' + ')}</span>}
                                            {ws.map((w) => (
                                              <button
                                                key={w.id}
                                                onClick={(e) => {
                                                  e.stopPropagation()
                                                  openWorker(w.id)
                                                }}
                                                title={`Worker “${w.name}” holds ${w.open} of these`}
                                                className="inline-flex max-w-[130px] items-center gap-1 rounded bg-[var(--color-inset)] px-1.5 py-px text-fg-muted hover:text-fg"
                                              >
                                                <Workflow size={9} className="shrink-0" />
                                                <span className="truncate">{w.name}</span>
                                                <span className="num text-fg-faint">{w.open}</span>
                                              </button>
                                            ))}
                                          </div>
                                        )}
                                      </td>
                                      <td className="num px-3 py-2.5 text-right text-fg-muted">{qtyText(h.quantity)}</td>
                                      <td className="num px-3 py-2.5 text-right text-fg-muted">{fmtPrice(h.avgCost)}</td>
                                      <td className="num px-3 py-2.5 text-right text-fg">{fmtPrice(h.last)}</td>
                                      <td className="px-3 py-2.5 text-right">
                                        <div className={cn('num', dirClass(h.dayPnl))}>{signed(h.dayPnl)}</div>
                                        <div className={cn('num text-[11px]', dirClass(h.dayPnl))}>{fmtPct(h.dayPct)}</div>
                                      </td>
                                      <td className="num px-3 py-2.5 text-right text-fg">{fmtMoney(h.value)}</td>
                                      <td className="px-3 py-2.5 text-right">
                                        <div className={cn('num', dirClass(h.openPnl))}>{signed(h.openPnl)}</div>
                                        <div className={cn('num text-[11px]', dirClass(h.openPnl))}>{fmtPct(h.openPnlPct)}</div>
                                      </td>
                                      <td className="num hidden px-3 py-2.5 text-right text-[11.5px] leading-tight md:table-cell">
                                        <div className={cn(slHit ? 'text-down' : 'text-fg-subtle')}>{h.stopLoss != null ? `SL ${fmtPrice(h.stopLoss)}` : <span className="text-fg-faint">SL —</span>}</div>
                                        <div className={cn(tpHit ? 'text-up' : 'text-fg-subtle')}>{h.takeProfit != null ? `TP ${fmtPrice(h.takeProfit)}` : <span className="text-fg-faint">TP —</span>}</div>
                                      </td>
                                      <td className="num hidden px-3 py-2.5 text-right text-fg-muted sm:table-cell">{h.weight.toFixed(0)}%</td>
                                      <td className="px-1 py-2.5 text-right">
                                        {manualOnly && (
                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation()
                                              if (confirm(`Remove ${h.symbol} from your recorded holdings?`)) void portfolioRemove(h.code).then(changed)
                                            }}
                                            className="rounded p-1 text-fg-subtle opacity-0 transition-opacity hover:text-down group-hover:opacity-100"
                                            title="Remove"
                                          >
                                            <Trash2 size={13} />
                                          </button>
                                        )}
                                      </td>
                                    </tr>
                                  )
                                })}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </>
                    )}
                    {tab === 'orders' && <Orders orders={p.orders} accounts={accounts} showAccount={many && !scope} />}
                    {tab === 'activity' && <Activity fills={p.fills} accounts={accounts} showAccount={many && !scope} />}
                  </div>
                </div>

                <div className="min-w-0 self-start space-y-5 lg:sticky lg:top-2">
                  <OrderTicket symbol={selected} onSymbolChange={setSelected} portfolio={all} scope={scope} onChanged={changed} />
                  <WatchlistCard />
                  <TickerOptions />
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
